/**
 * MapView — Google Maps wrapper for the H-Plate admin portal.
 *
 * The original Manus-generated component proxied the Google Maps script
 * through Manus's internal `forge.butterfly-effect.dev` service. That
 * proxy stops responding once the project leaves Manus's runtime, which
 * is why the Live Map went blank after we cut the cord.
 *
 * This rewrite loads the script directly from `maps.googleapis.com` using
 * your own API key (`VITE_GOOGLE_MAPS_API_KEY`). Best-practice key
 * hygiene:
 *
 *   - Use a SEPARATE Google Cloud API key for the web admin (different
 *     from your mobile EXPO_PUBLIC_GOOGLE_MAPS_API_KEY).
 *   - Restrict the web key to HTTP referrers:
 *       https://admin.h-plate.com/*
 *       https://h-plate.com/*
 *       https://www.h-plate.com/*
 *       https://*.pages.dev/*           ← so Cloudflare Pages preview URLs work
 *       http://localhost:3000/*         ← so `pnpm dev` works
 *   - Enable only "Maps JavaScript API", "Places API", "Geocoding API",
 *     "Directions API" for that key.
 *
 * The component keeps the same public API as the original (so the
 * `LiveMap.tsx`, `StopsMap.tsx`, `CorridorBuilder.tsx` callers don't
 * need to change) — only the script loader and the API key reference
 * changed.
 */

/// <reference types="@types/google.maps" />

import { useEffect, useRef } from 'react';
import { usePersistFn } from '@/hooks/usePersistFn';
import { cn } from '@/lib/utils';

declare global {
  interface Window {
    google?: typeof google;
  }
}

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

let _mapsLoadPromise: Promise<unknown> | null = null;

function loadMapScript() {
  // Already loaded — resolve immediately.
  if (window.google?.maps) {
    return Promise.resolve(null);
  }
  if (_mapsLoadPromise) {
    return _mapsLoadPromise;
  }

  _mapsLoadPromise = new Promise((resolve, reject) => {
    if (!API_KEY) {
      const msg =
        '[MapView] Missing VITE_GOOGLE_MAPS_API_KEY. Add it to web-admin/.env and to Cloudflare Pages env vars.';
      console.error(msg);
      reject(new Error(msg));
      return;
    }

    const script = document.createElement('script');
    script.src =
      `https://maps.googleapis.com/maps/api/js` +
      `?key=${encodeURIComponent(API_KEY)}` +
      `&v=weekly` +
      `&libraries=marker,places,geocoding,geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(null);
    script.onerror = () => {
      console.error('[MapView] Failed to load Google Maps script.');
      _mapsLoadPromise = null;
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(script);
  });

  return _mapsLoadPromise;
}

interface MapViewProps {
  className?: string;
  initialCenter?: google.maps.LatLngLiteral;
  initialZoom?: number;
  onMapReady?: (map: google.maps.Map) => void;
  /** Hide map-type, Street View and fullscreen controls (public pages). */
  minimalControls?: boolean;
}

export function MapView({
  className,
  initialCenter = { lat: 15.4150, lng: -61.3710 }, // Center of Dominica
  initialZoom = 10,
  onMapReady,
  minimalControls = false,
}: MapViewProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);

  const init = usePersistFn(async () => {
    try {
      await loadMapScript();
    } catch (e) {
      // Already logged in loadMapScript — bail without throwing further
      // so the surrounding component can show its empty state.
      return;
    }
    if (!mapContainer.current) {
      console.error('[MapView] Map container not mounted yet.');
      return;
    }
    map.current = new window.google!.maps.Map(mapContainer.current, {
      zoom: initialZoom,
      center: initialCenter,
      mapTypeControl: !minimalControls,
      fullscreenControl: !minimalControls,
      zoomControl: true,
      streetViewControl: !minimalControls,
      clickableIcons: !minimalControls,
      // mapId is required for AdvancedMarkerElement. "DEMO_MAP_ID" is
      // a Google-published demo id that works without setup. Replace
      // with a custom Cloud-styled Map ID when you create one in the
      // Google Cloud Console (Maps → Map Management).
      mapId: 'DEMO_MAP_ID',
    });
    if (onMapReady && map.current) {
      onMapReady(map.current);
    }
  });

  useEffect(() => {
    init();
  }, [init]);

  return (
    <div ref={mapContainer} className={cn('w-full h-[500px]', className)} />
  );
}
